/**
 * Composer attach pickers (PlayAd "+" menu):
 *   generated   — "Generated & Uploaded": creatives.list { brandId, source } media (generated + uploaded, not imported)
 *   creations   — "Creations": every creative incl. imported ad-account ads (creatives.list { brandId })
 *   inspiration — "Inspiration": saved references (references.list { brandId, saved: true })
 */
import { Check, Film, Images, Lightbulb, Rocket } from "lucide-react";
import { useMemo, useState } from "react";
import { useTool } from "@/api/hooks";
import { creativeSource, creativeThumb, pickArray, type Creative, type ReferenceItem } from "@/api/types";
import { cn } from "@/lib/cn";
import { Button } from "../ui/Button";
import { EmptyState, ErrorState, Skeleton } from "../ui/Feedback";
import { SearchInput } from "../ui/Form";
import { Modal } from "../ui/Overlay";
import { CreativeMedia, CreativeStatusPill } from "../creative/CreativeBits";
import { isVideoUrl } from "./util";

export type LibrarySource = "generated" | "creations" | "inspiration";

export interface PickedMedia {
  source: LibrarySource;
  id: string;
  name: string;
  url: string | null;
  mediaType: "image" | "video";
}

const META: Record<LibrarySource, { title: string; description: string; empty: string; icon: React.ReactNode }> = {
  generated: { title: "Generated & Uploaded", description: "Media you generated or uploaded for this brand.", empty: "Nothing generated or uploaded yet.", icon: <Images className="size-5" /> },
  creations: { title: "Creations", description: "Every creative in the library, including imported ads.", empty: "No creations yet.", icon: <Rocket className="size-5" /> },
  inspiration: { title: "Inspiration", description: "References you saved from the Inspiration library.", empty: "No saved references yet — save ads from Inspiration first.", icon: <Lightbulb className="size-5" /> },
};

export function LibraryPicker({
  brandId,
  source,
  open,
  onClose,
  onPick,
  multiple = true,
  max = 10,
}: {
  brandId: string;
  source: LibrarySource | null;
  open: boolean;
  onClose: () => void;
  onPick: (items: PickedMedia[]) => void;
  multiple?: boolean;
  max?: number;
}) {
  const isRef = source === "inspiration";
  const creativesQ = useTool<unknown>("creatives.list", open && source && !isRef ? { brandId, limit: 120 } : null, { staleTime: 30_000 });
  const refsQ = useTool<unknown>("references.list", open && isRef ? { brandId, saved: true, limit: 120 } : null, { staleTime: 30_000 });
  const q = isRef ? refsQ : creativesQ;
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<string[]>([]);

  const items = useMemo<(PickedMedia & { sub?: string; status?: string })[]>(() => {
    if (!source) return [];
    if (isRef) {
      const refs = pickArray<ReferenceItem>(refsQ.data, "references");
      const saved = refs.some((r) => r.saved === true) ? refs.filter((r) => r.saved) : refs;
      return saved.map((r) => {
        const url = r.thumbnailUrl ?? r.mediaUrl ?? null;
        return {
          source,
          id: r.id,
          name: r.pageName ?? r.creator ?? r.headline ?? "Reference",
          url,
          mediaType: r.mediaType === "video" && !r.thumbnailUrl ? "video" : r.mediaType === "video" ? "video" : "image",
          sub: r.daysRunning ? `${r.daysRunning} days running` : (r.source ?? undefined),
        };
      });
    }
    const all = pickArray<Creative>(creativesQ.data, "creatives");
    const list = source === "generated" ? all.filter((c) => creativeSource(c) !== "imported") : all;
    return list.map((c) => ({ source, id: c.id, name: c.name, url: creativeThumb(c), mediaType: c.kind === "video" ? "video" : "image", status: c.status, sub: source === "generated" ? (creativeSource(c) === "uploaded" ? "Uploaded" : "Generated") : undefined }));
  }, [source, isRef, refsQ.data, creativesQ.data]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? items.filter((i) => `${i.name} ${i.sub ?? ""}`.toLowerCase().includes(s)) : items;
  }, [items, search]);

  const close = () => {
    setSel([]);
    setSearch("");
    onClose();
  };
  if (!source) return null;
  const meta = META[source];
  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={meta.title}
      description={meta.description}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!sel.length}
            onClick={() => {
              onPick(items.filter((i) => sel.includes(i.id)).map(({ source: s, id, name, url, mediaType }) => ({ source: s, id, name, url, mediaType })));
              close();
            }}
          >
            Attach{sel.length ? ` · ${sel.length}` : ""}
          </Button>
        </>
      }
    >
      <SearchInput value={search} onChange={setSearch} placeholder={`Search ${meta.title.toLowerCase()}…`} className="mb-4" autoFocus />
      {q.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/5] rounded-xl" />
          ))}
        </div>
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />
      ) : !filtered.length ? (
        <EmptyState compact icon={meta.icon} title={items.length ? "No matches" : meta.empty} body={items.length ? "Try a different search." : undefined} />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {filtered.map((it) => {
            const on = sel.includes(it.id);
            const full = !on && sel.length >= max;
            return (
              <button
                key={it.id}
                type="button"
                aria-pressed={on}
                disabled={full}
                onClick={() => setSel((s) => (on ? s.filter((x) => x !== it.id) : multiple ? [...s, it.id] : [it.id]))}
                className={cn("group flex flex-col gap-1.5 rounded-xl p-1 text-left transition-colors hover:bg-hover disabled:opacity-40", on && "bg-primary-soft")}
              >
                <div className={cn("relative aspect-[4/5] overflow-hidden rounded-lg border border-line bg-[#151517]", on && "ring-2 ring-primary")}>
                  <CreativeMedia url={it.url} mediaType={it.mediaType === "video" && isVideoUrl(it.url) ? "video" : "image"} className="size-full" alt={it.name} />
                  {it.mediaType === "video" && (
                    <span className="absolute bottom-1.5 left-1.5 grid size-6 place-items-center rounded-full bg-black/60 text-white">
                      <Film className="size-3" />
                    </span>
                  )}
                  {on && (
                    <span className="absolute right-1.5 top-1.5 grid size-5 place-items-center rounded-full bg-primary text-white">
                      <Check className="size-3" strokeWidth={3} />
                    </span>
                  )}
                </div>
                <div className="truncate px-0.5 text-[12.5px] font-medium">{it.name}</div>
                {it.status ? <CreativeStatusPill status={it.status} size="xs" className="self-start" /> : it.sub ? <div className="truncate px-0.5 text-[11.5px] text-muted">{it.sub}</div> : null}
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
